import Image from "../../components/Image";
import { Box, Link, Typography } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import styles from "./Footer.styles";

const Footer = () => {
  const { translate, selectedLanguage } = useLanguage();
  const location = useLocation();
  const isAuthPage = location.pathname === "/sign";
  const contactEmail =
    process.env.REACT_APP_BUSINESS_EMAIL || "info@sharp-view.ca";
  const isRTL = selectedLanguage === "ar";
  const columns = [
    {
      title: translate("footer explore"),
      links: [
        { label: translate("platform"), to: "/#platform" },
        { label: translate("features"), to: "/#features" },
        { label: translate("pricing"), to: "/#pricing" },
        { label: translate("faq"), to: "/#faq" },
      ],
    },
    {
      title: translate("footer get started"),
      links: [
        { label: translate("footer create account"), to: "/sign?mode=signup" },
        { label: translate("sign in"), to: "/sign" },
        { label: translate("downloads"), to: "/downloads" },
        { label: translate("request a demo"), to: "/request-demo" },
      ],
    },
    {
      title: translate("company"),
      links: [
        { label: translate("footer about"), to: "/#about-sharp-view" },
        { label: translate("footer contact"), href: `mailto:${contactEmail}` },
        { label: translate("footer privacy"), to: "/privacy-policy" },
        { label: translate("footer terms"), to: "/terms" },
      ],
    },
  ];

  if (isAuthPage) {
    return (
      <Box
        component="footer"
        sx={{ ...styles.Footer_authContainer, direction: isRTL ? "rtl" : "ltr" }}
      >
        <Typography sx={styles.Footer_authText}>
          © {new Date().getFullYear()} Sharp View
        </Typography>
        <Box sx={styles.Footer_authLinks}>
          <Link component={RouterLink} to="/privacy-policy" sx={styles.Footer_authLink}>
            {translate("footer privacy")}
          </Link>
          <Link component={RouterLink} to="/terms" sx={styles.Footer_authLink}>
            {translate("footer terms")}
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="footer"
      sx={{ ...styles.Footer_container, direction: isRTL ? "rtl" : "ltr" }}
    >
      <Box sx={styles.Footer_grid}>
        <Box sx={styles.Footer_brand}>
          <Box sx={styles.Footer_logoContainer}>
            <Image
              src={require("../../assets/logo.webp")}
                srcSet={`${require("../../assets/logo-small.webp")} 380w, ${require("../../assets/logo.webp")} 540w`}
                sizes="(max-width: 599px) 230px, 270px"
              width={540}
              height={97}
              alt="Sharp View"
              loading="lazy"
              decoding="async"
              sx={styles.Footer_logo}
            />
          </Box>
          <Typography sx={styles.Footer_description}>
            {translate("company description")}
          </Typography>
        </Box>

        {columns.map((column, index) => (
          <Box
            key={column.title}
            component="nav"
            aria-label={column.title}
            sx={{
              ...styles.Footer_navColumn,
              ...(index === columns.length - 1
                ? styles.Footer_companyColumn
                : {}),
            }}
          >
            <Typography sx={styles.Footer_linksHeader}>
              {column.title}
            </Typography>
            <Box
              sx={{
                ...styles.Footer_links,
                ...(index === columns.length - 1
                  ? styles.Footer_companyLinks
                  : {}),
              }}
            >
              {column.links.map((link) =>
                link.href ? (
                  <Link
                    key={link.label}
                    href={link.href}
                    sx={styles.Footer_linkStyle}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <Link
                    key={link.label}
                    component={RouterLink}
                    to={link.to}
                    sx={styles.Footer_linkStyle}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={styles.Footer_divider} />
      <Box sx={styles.Footer_socialLinks}>
        <Link
          href="https://www.linkedin.com/company/sharp-view"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Sharp View on LinkedIn"
          sx={styles.Footer_socialLink}
        >
          <LinkedInIcon />
        </Link>
        <Link
          href="https://www.facebook.com/SharpViewSoftware/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Sharp View on Facebook"
          sx={styles.Footer_socialLink}
        >
          <FacebookIcon />
        </Link>
        <Link
          href="https://www.instagram.com/sharpview.ca/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Sharp View on Instagram"
          sx={styles.Footer_socialLink}
        >
          <InstagramIcon />
        </Link>
      </Box>
    </Box>
  );
};

export default Footer;
